import moment from 'moment';

/**
 * Handles obtaining the JSON object of a restaurant's menu
 *
 * Injections:
 *   $http : to make the API request for the menus
 */
export function MenuService($http, $q) {
    'ngInject';
    let svc = { getMenus, validateItemStock };
    let menus = null;
    let menuItemIds = null;
    let oosItems = null;
    let curFulfillmentDate;

    return svc;

    //------------------
    /**
     * flattenAndPruneGroup(): take a group hierarchy and flatten it into a single array
     *  -> Removes groups that should not be shown
     *  -> Removed items in groups that should not be shown
     *  -> Side Effect: group names will be converted to hierarchical names
     *  -> Format will follow "group / subgroup / sub-subgroup" ... etc.
     *  @param hierarchicalName: name of the parent group to be built upon (recursive)
     *  @param group: group to be flattened and pruned
     */
    function flattenAndPruneGroup(hierarchicalName, group) {
        var i,
            divider = ' / ',
            prunedGroups = [],
            prunedItems = [],
            flattenedSubgroups;

        // Update hierarchical name
        if (hierarchicalName.length > 0) {
            hierarchicalName = hierarchicalName + divider + group.name;
        } else {
            hierarchicalName = group.name;
        }
        group.name = hierarchicalName;

        // Remove items from the group that are not orderable
        for (i = 0; i < group.items.length; i++) {
            prunedItems.push(group.items[i]);
        }
        group.items = prunedItems;

        // Flatten subgroups of group into the 'groups' array
        for (i = 0; i < group.subgroups.length; i++) {
            flattenedSubgroups = flattenAndPruneGroup(hierarchicalName, group.subgroups[i]);

            // Add the subgroups to the current group array, thus flattening it
            prunedGroups = prunedGroups.concat(flattenedSubgroups);
        }

        // Clear the subgroups of the group (to make the menu object cleaner)
        group.subgroups = [];

        // Only add a group if it has items
        if (group.items.length > 0) {
            // Add parent group to the front of the list of groups
            prunedGroups.unshift(group);
        }

        return prunedGroups;
    }

    /**
     * flattenAndPruneMenus(): Flattens each group in each menu, and removes any menus we don't want to show
     *  -> Menus not orderable online will be removed from the list
     *  -> Returns a list of menus that have a single groups array, with no nesting of subgroups
     *  @param menus: object tree of all menus that we will traverse
     */
    function flattenAndPruneMenus(menus) {
        var i,
            j,
            hierarchicalName,
            flattenedGroups,
            prunedMenus = [];

        for (i = 0; i < menus.length; i++) {
            hierarchicalName = '';

            flattenedGroups = [];
            // Flatten each group in this menu and add the groups to an array
            for (j = 0; j < menus[i].groups.length; j++) {
                // flattenedGroups will have all the original groups as well as flattened subgroups
                flattenedGroups = flattenedGroups.concat(flattenAndPruneGroup(hierarchicalName, menus[i].groups[j]));
            }

            /**
             * If there is only 1 group in the menu, we want to hide the group's header name
             * Creating a flag to indicate this
             */
            if (flattenedGroups.length === 1) {
                flattenedGroups[0].hideHeader = true;
            }

            menus[i].groups = flattenedGroups;
            prunedMenus.push(menus[i]);
        }
        return prunedMenus;
    }

    /**
     * getMenus(): either return the loaded menus or loads the menu through an API call
     *  -> will load the menus again if the fulfillmentDate was different than the last call
     */
    function getMenus(fulfillmentDate) {
        var deferred = $q.defer();

        // If fulfillmentDate is null, use the current time
        const isoFulfillmentDate = fulfillmentDate || moment().toISOString();

        if (!menus || curFulfillmentDate !== fulfillmentDate) {
            curFulfillmentDate = fulfillmentDate;
            $http({
                method: 'GET',
                url: 'menus',
                params: {
                    fulfillmentDate: isoFulfillmentDate
                }
            }).then(function(response) {
                // Returns the pruned json response object to the menuController
                menus = flattenAndPruneMenus(response.data);

                deferred.resolve(menus);
            });
        } else {
            deferred.resolve(menus);
        }

        return deferred.promise;
    }

    /**
     * getMenuItemIds(): returns a list of menu item ids from menuService.menus
     */
    function getMenuItemIds() {
        if (!menuItemIds) {
            menuItemIds = new Set();

            menus.forEach(function(menu) {
                menu.groups.forEach(function(group) {
                    group.items.forEach(function(item) {
                        menuItemIds.add(item.idString);
                    });
                });
            });
        }

        return menuItemIds;
    }

    /**
     * validateItemStock(): return any items that are out of stock from the given list of menuItems
     *  @param menuItems: list of menu item ids
     */
    function validateItemStock() {
        var deferred = $q.defer();

        if (!oosItems) {
            var payload = { items: getMenuItemIds() };
            $http({
                method: 'POST',
                url: 'validatestock',
                data: JSON.stringify(payload)
            }).then(function(response) {
                oosItems = response.data;

                deferred.resolve(oosItems);
            });
        } else {
            deferred.resolve(oosItems);
        }

        return deferred.promise;
    }
}
